import { updateTask } from './taskService';
import { normalizeRole } from '../config/roles';

// Automatic tasks are Notification rows with auto send on.
// They are handled by the system, so they stay off personal task lists
// and only appear in a transaction's All view.

// Client emails always need approval (Oct 27), so they are NOT automatic: they show on My tasks with a note.
// Mirrors ALWAYS_CLIENT_EMAILS in functions/email/approvals.js.
const CLIENT_EMAIL_NAMES = [/seller inspection prep/i, /introduc\w* transaction coordinator/i, /closing prep to seller/i,
  /congratulations we are under contract/i, /external client congratulations/i, /closing week checklist to client/i,
  /prepare buyer for final walk through/i, /insurance recommendation email/i, /^external email/i];
export const isClientEmailTask = (task) => {
  const name = String(task?.name || task?.title || '');
  const rc = Array.isArray(task?.recipients) ? task.recipients : [];
  const isEmail = task?.type === 'Notification' || /email/i.test(name);
  return isEmail && (rc.includes('Client') || CLIENT_EMAIL_NAMES.some((re) => re.test(name)));
};
export const isAutomatic = (task) => (task.automatic === true || task.mode === 'auto') && !isClientEmailTask(task);

// My tasks: manual tasks whose role matches the user's role.
// Tasks without a role are treated as Agent tasks.
// Admins who also work their own deals count as Agents too.
// The primary agent and any co-agent (coListAgents / coSellingAgents) on a deal both work its Agent tasks.
export const myRolesFor = (userRole, opts = {}) => {
  // A Limited Agent works the Agent tasks on her deals, the same as an Agent.
  const roles = userRole === 'Admin' ? ['Admin', 'Agent'] : userRole === 'limited_agent' ? ['Agent'] : [userRole];
  return opts.dealAgent && !roles.includes('Agent') ? [...roles, 'Agent'] : roles;
};

// True when uid is the deal owner or listed as a co-list / co-selling agent on it.
export const isDealAgent = (deal, uid, ownerUid) => Boolean(uid) && (
  (ownerUid || deal?.ownerUid) === uid
  || [...(deal?.coListAgents || []), ...(deal?.coSellingAgents || [])].some(a => a && a.uid === uid)
);

// Transaction tasks that belong to this user: for everyone on the deal, or for one of the user's roles.
export const isRoleMine = (task, userRole, opts = {}) => !hiddenFromLimited(task, userRole) && (task.appliesTo === 'All' || myRolesFor(userRole, { dealAgent: opts.dealAgent ?? task.viewerIsDealAgent }).includes(task.role || 'Agent'));

// Quick-action email tasks: any email/text-to-send task (Notification rows, client emails, MANUAL SEND, approvals).
// On a Limited Agent's deals these are hidden from her completely; they show only for the lead agent (Kelly).
export const isQuickEmailTask = (task) => {
  const name = String(task?.name || task?.title || '');
  return task?.type === 'Notification' || isClientEmailTask(task) || /^(manual send|auto email|external email|approve)/i.test(name) || /\b(e-?mail|text)\b/i.test(name) && /^(send|text|email)/i.test(name);
};
// Auto Email tasks are sent by Resi and never shown on any task list, for any role (sending is unchanged).
export const isAutoEmailTask = (task) => /^\s*auto[\s-]*email/i.test(String(task?.name || task?.title || '')) || ['auto email', 'auto-email', 'autoemail', 'auto_email'].includes(String(task?.type || '').trim().toLowerCase());
// Used by every task view: hides Auto Email tasks for everyone, and quick-action email tasks from Limited Agents.
export const hiddenFromLimited = (task, userRole) => isAutoEmailTask(task) || (normalizeRole(userRole) === 'limited_agent' && isQuickEmailTask(task));

// Local assistants and marketing managers only see their own role's tasks in My tasks, even when a task is shared.
const ROLES_WITHOUT_SHARED = ['Local Assistant', 'Marketing Manager'];
export const isMyTask = (task, userRole, opts = {}) => !isAutomatic(task) && !hiddenFromLimited(task, userRole) && (
  myRolesFor(userRole, { dealAgent: opts.dealAgent ?? task.viewerIsDealAgent }).includes(task.role || 'Agent') || (task.shared === true && !ROLES_WITHOUT_SHARED.includes(userRole))
);

// Marks an automatic task done once its message has gone out.
export const markAutomaticSent = (userId, taskId) => {
  const now = new Date().toISOString();
  return updateTask(userId, taskId, { status: 'completed', completedAt: now, sentAt: now });
};
